import React, { useMemo, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ExpandMoreIcon } from './Icons';
import { Checkbox, Dropdown, Menu } from 'antd';
import { useSelector } from 'react-redux';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';

const CustomDropdown = ({
  className,
  overlayClassName,
  open,
  onClick = () => {},
  disabled,
  menuItems = [],
  selectedKeys = [],
  handleChange,
  menuProps = {},
  children,
}) => {
  const trans = useSelector(translator);
  const [isVisible, setIsVisible] = useState(open);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsVisible(false);
        onClick(event); // Pass event object to onClick callback
      }
    };
    // Only attach the event listener when the dropdown is visible
    if (isVisible) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isVisible]);

  const selectedLabel = useMemo(() => {
    const selection = menuItems.filter((a) => selectedKeys.includes(a.key));
    if (!selectedKeys.length) return trans('SELECTFROMOPTIONS');
    const separator = selection.length > 1 ? ', ' : null;
    const label = selection.reduce((result, a, index) => {
      const safeLabel = a.rawLabel || a.label;
      result += safeLabel + (separator && index !== selection.length - 1 ? separator : '');
      return result;
    }, '');
    return label;
  }, [selectedKeys, menuItems]);

  // Function to handle click on the dropdown
  const handleDropdownClick = (event) => {
    if (disabled) return; // Prevent any interaction when disabled
    setIsVisible(!isVisible);
  };

  // Render checkboxes in the dropdown
  const renderMenu = (
    <Menu>
      {menuItems.map((item) => (
        <Menu.Item
          key={item.key}
          className={selectedKeys.includes(item.key) ? 'highlight-label' : ''}
        >
          <Checkbox
            checked={selectedKeys.includes(item.key)}
            onChange={() => handleChange(item.key)}
          >
            <span onClick={(e) => e.stopPropagation()}>{item.renderLabel || item.label}</span>
          </Checkbox>
        </Menu.Item>
      ))}
    </Menu>
  );

  return (
    <Dropdown
      overlay={renderMenu}
      overlayClassName={`custom-dropdown-style ${overlayClassName ?? ''}`}
      open={isVisible}
      disabled={disabled}
      {...menuProps}
    >
      <div
        ref={dropdownRef}
        className={`custom-dropdown ${className ?? ''} ${disabled ? 'disabled' : ''}`}
        onClick={handleDropdownClick}
      >
        <div className="dropdown-label">
          {children ? (
            children
          ) : (
            <span className="text" title={selectedLabel}>
              {selectedLabel}
            </span>
          )}
        </div>
        {isVisible ? (
          <ExpandMoreIcon className="status-icon rotate-180" />
        ) : (
          <ExpandMoreIcon className="status-icon" />
        )}
      </div>
    </Dropdown>
  );
};

CustomDropdown.propTypes = {
  className: PropTypes.string,
  overlayClassName: PropTypes.string,
  open: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  menuItems: PropTypes.arrayOf(PropTypes.object),
  selectedKeys: PropTypes.arrayOf(PropTypes.string),
  handleChange: PropTypes.func,
  menuProps: PropTypes.object,
};

export default CustomDropdown;
